'use client';

/**
 * CostPlanSurface — registry-driven peer rail surface showing the
 * currently PINNED cost plan's (MCP) line items.
 *
 * Fourth-ish peer in the right rail column alongside Insights / Ask /
 * Composer / Chat — toggled via `useRailSurfaceStore.active ===
 * 'cost_plan'`, matrix-gated per mode via `cost_plan_surface_cards`
 * (mirrors `composer_surface_cards`'s mode-level, self-gating shape).
 *
 * Lets a designer browsing Discovery (mode3) or Products (mode11) pin
 * an already-open cost plan, see its line items without leaving the
 * product-browsing canvas, click a line to focus its requirement
 * (`focused-requirement-store.ts`), then add a product tile straight
 * to that requirement via the product tile's second "Add to Cost
 * Plan" icon (`product-tile.tsx`, `useMcpLineAppend`).
 *
 * Mounts `ObeyaCostPlanRequirementsCard` (`obeya_costplan_requirements`)
 * — a read-only requirement glance list, forced to `group_by="line"`
 * (the requirement grouping — see `build_costplan_pivot`'s Line mode
 * groups by `source_line_sid`). This is NOT mode1 canvas's
 * `ObeyaCostPlanLinesCard` (the full qty/price/room-assignment editor,
 * `obeya_costplan_lines`) reused with props — an earlier version did
 * that via two opt-in props (`compact`, `interactive`) that only
 * trimmed chrome, leaving every editing affordance live in a narrow
 * rail column. `ObeyaCostPlanRequirementsCard` is a structurally
 * separate, read-only component sharing the same `costplan_pivot` data
 * source — see its own docstring. Replaces the EARLIER temporary reuse
 * of the flat `mcp_details` rail card (`EntityDetailsCard` bound to
 * `costplan_details`) — that card has no grouping concept, so a
 * 104-line plan showed 107 individual candidate rows instead of the
 * ~38 requirements they group into.
 *
 * Picker (nothing pinned yet): reuses the SAME two strategies the
 * mode1 Projects explorer already uses for its project/MCP tree —
 * `obeya`/`list` (project search) and `project_intelligence`/`explore`
 * (per-project MCP list) — rather than minting a new backend strategy.
 *
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 * @see klyde_ipe/components/explorer/sections/obeya-projects-list-section.tsx
 *   — the picker's data-source precedent (same two hooks, same strategies)
 *
 * @component_role Sibling rail surface
 * @component_type Panel
 * @domain Frontend Shell / Cost Plans
 * @layer presentation
 */

import { useState } from 'react';
import { DollarSign, Search, LayoutGrid } from 'lucide-react';
import { useObeyaProjects, useProjectSubfolders } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useTotalMatrixStore, useCostPlanLabel } from '@/lib/stores/total-matrix-store';
import { usePinnedCostPlanStore } from '@/lib/stores/pinned-costplan-store';
import { useAgentCanvasStore } from '@/lib/stores/agent-canvas-store';
import { CardChromeProvider } from '@/lib/design/card-chrome';
import { CardPlacementProvider } from '@/lib/design/card-export';
import { ObeyaCostPlanRequirementsCard } from '@/components/canvas/cards/obeya/costplan-requirements-card';
import type { ObeyaListItem, ProjectExploreData } from '@/lib/api/types';

export function CostPlanSurface() {
  const { mode } = useViewportSelection();
  const getModeConfig = useTotalMatrixStore((s) => s.getModeConfig);
  const cardKeys = getModeConfig(mode)?.cost_plan_surface_cards ?? [];
  const pinnedMcpId = usePinnedCostPlanStore((s) => s.pinnedMcpId);
  const setPinnedMcpId = usePinnedCostPlanStore((s) => s.setPinnedMcpId);
  const unavailableTitle = useCostPlanLabel('unavailable_title');
  const unavailableBody = useCostPlanLabel('unavailable_body');

  // Mode hasn't opted in — self-gate (mirrors ComposerSurface's
  // `composer_surface_cards.length === 0` empty state).
  if (cardKeys.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-panel text-muted-foreground p-6">
        <DollarSign className="h-8 w-8 mb-2 opacity-40" />
        <p className="text-sm">{unavailableTitle}</p>
        <p className="text-xs mt-1 text-center">{unavailableBody}</p>
      </div>
    );
  }

  return pinnedMcpId ? (
    <PinnedCostPlan mcpId={pinnedMcpId} />
  ) : (
    <CostPlanPicker onPin={({ mcpId }) => setPinnedMcpId(mcpId)} />
  );
}

/** Pinned state — the `mcp_details` card, parameterized by the pin. */
function PinnedCostPlan({ mcpId }: { mcpId: string }) {
  const clearPinnedMcpId = usePinnedCostPlanStore((s) => s.clearPinnedMcpId);
  const surfaceTitle = useCostPlanLabel('surface_title');
  const unpinLabel = useCostPlanLabel('pinned_unpin_action');
  const focusHint = useCostPlanLabel('pinned_focus_hint');
  const viewGroupedLabel = useCostPlanLabel('view_grouped_action');
  // Opens the Cost Plan review overlay (grouped-requirement pivot,
  // obeya_costplan_lines) — the SAME canvas card mode1's own 'mcp'
  // selection ships, parameterized here by the pin rather than page
  // selection. Always shown while a plan is pinned: unlike
  // McpWatchWindowCard's "Review changes" (gated on
  // getViewportConfig(mode, contentSelection) actually shipping an
  // overlay for the CURRENT selection), this overlay's content is a
  // fixed, explicit (mode, 'cost_plan_review') lookup — not selection-
  // dependent — so there's no "wrong selection, no overlay" case to
  // gate against here.
  const openCostPlanReview = useAgentCanvasStore((s) => s.openCostPlanReview);

  return (
    // Flex column, not the earlier `overflow-y-auto` block — the card
    // below scrolls its OWN table body internally (sticky toolbar +
    // header stay put while rows scroll), so this container needs a
    // bounded height to hand it rather than growing with content.
    <div className="h-full flex flex-col bg-panel">
      <div className="flex items-center justify-between px-3 py-2 gap-2 shrink-0">
        <p className="text-[11px] text-muted-foreground">{focusHint}</p>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => openCostPlanReview()}
            className="flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <LayoutGrid className="h-3 w-3" />
            {viewGroupedLabel}
          </button>
          <button
            type="button"
            onClick={() => clearPinnedMcpId()}
            className="text-xs text-muted-foreground hover:text-foreground hover:underline shrink-0"
          >
            {unpinLabel}
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <CardChromeProvider
          contentKey="obeya_costplan_requirements"
          label={surfaceTitle}
          iconName="List"
          strategy="costplan_pivot"
        >
          <CardPlacementProvider value={{ surface: 'rail', slot_id: 'obeya_costplan_requirements' }}>
            <ObeyaCostPlanRequirementsCard />
          </CardPlacementProvider>
        </CardChromeProvider>
      </div>
    </div>
  );
}

/** One MCP pick from the picker — everything a caller needs to either
 *  pin it (cost-plan-surface's own use) or target it for a lightweight
 *  append (Composer's `targetMcpForAppend`), without the picker itself
 *  knowing which. */
export interface CostPlanPickerPick {
  mcpId: string;
  mcpName: string;
  projectSid: string;
  projectName: string;
}

/**
 * Picker — search projects (reusing `obeya`/`list`, same strategy +
 * hook the Projects explorer's project tree already uses), expand one
 * to its cost plans (`project_intelligence`/`explore`), pin one.
 *
 * `onPin` is the only side effect the picker performs — parameterized
 * so a second caller (Composer's "target an existing plan" affordance)
 * can reuse the exact same search→expand→pick UI with a different
 * outcome (`targetMcpForAppend` instead of `usePinnedCostPlanStore`),
 * rather than forking the component.
 */
export function CostPlanPicker({ onPin }: { onPin: (pick: CostPlanPickerPick) => void }) {
  const [search, setSearch] = useState('');
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);

  const listStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const { data, isLoading } = useObeyaProjects(listStrategy ?? '', { limit: 50 });

  const emptyTitle = useCostPlanLabel('picker_empty_title');
  const emptyBody = useCostPlanLabel('picker_empty_body');
  const searchPlaceholder = useCostPlanLabel('picker_search_placeholder');
  const projectsEmpty = useCostPlanLabel('picker_projects_empty');
  const loadingLabel = useCostPlanLabel('picker_loading');

  const needle = search.trim().toLowerCase();
  const projects = (data?.projects ?? []).filter(
    (p) => needle === '' || p.name.toLowerCase().includes(needle),
  );

  return (
    <div className="h-full overflow-y-auto bg-panel p-3 space-y-3">
      <div className="flex flex-col items-center text-center gap-1 py-2">
        <DollarSign className="h-6 w-6 text-muted-foreground opacity-60" />
        <p className="text-sm font-medium">{emptyTitle}</p>
        <p className="text-xs text-muted-foreground">{emptyBody}</p>
      </div>

      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full pl-7 pr-2 py-1.5 text-sm rounded border border-border bg-background"
        />
      </div>

      {isLoading && <div className="text-xs text-muted-foreground px-1">{loadingLabel}</div>}
      {!isLoading && projects.length === 0 && (
        <div className="text-xs text-muted-foreground px-1">{projectsEmpty}</div>
      )}

      <div className="space-y-1">
        {projects.map((project) => (
          <ProjectPickerRow
            key={project.semantic_id}
            project={project}
            expanded={expandedProjectId === project.semantic_id}
            onToggle={() =>
              setExpandedProjectId((cur) => (cur === project.semantic_id ? null : project.semantic_id))
            }
            onPin={onPin}
          />
        ))}
      </div>
    </div>
  );
}

function ProjectPickerRow({
  project,
  expanded,
  onToggle,
  onPin,
}: {
  project: ObeyaListItem;
  expanded: boolean;
  onToggle: () => void;
  onPin: (pick: CostPlanPickerPick) => void;
}) {
  const exploreStrategy = useCapabilityStore((s) => s.strategyName('project_intelligence', 'explore'));
  const { data: response, isLoading } = useProjectSubfolders(
    exploreStrategy ?? '',
    expanded ? project.semantic_id : null,
  );
  const exploreData = response?.data as ProjectExploreData | undefined;
  const mcps = exploreData?.mcps ?? [];

  const mcpsEmpty = useCostPlanLabel('picker_mcps_empty');
  const loadingLabel = useCostPlanLabel('picker_loading');
  const pinLabel = useCostPlanLabel('picker_pin_action');
  const mcpCountLabel = useCostPlanLabel('picker_mcp_count_label', { count: project.mcp_count });

  return (
    <div className="rounded border border-border">
      <button
        type="button"
        onClick={onToggle}
        disabled={project.mcp_count === 0}
        className="w-full flex items-center justify-between px-2 py-1.5 text-sm text-left disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted"
      >
        <span className="truncate">{project.name}</span>
        <span className="text-xs text-muted-foreground shrink-0 ml-2">{mcpCountLabel}</span>
      </button>
      {expanded && (
        <div className="border-t border-border px-2 py-1.5 space-y-1">
          {isLoading && <div className="text-xs text-muted-foreground">{loadingLabel}</div>}
          {!isLoading && mcps.length === 0 && (
            <div className="text-xs text-muted-foreground">{mcpsEmpty}</div>
          )}
          {mcps.map((mcp) => (
            <button
              key={mcp.semantic_id}
              type="button"
              onClick={() =>
                onPin({
                  mcpId: mcp.semantic_id,
                  mcpName: mcp.name,
                  projectSid: project.semantic_id,
                  projectName: project.name,
                })
              }
              className="w-full flex items-center justify-between px-2 py-1 text-xs rounded hover:bg-muted"
            >
              <span className="truncate">{mcp.name}</span>
              <span className="text-primary shrink-0 ml-2">{pinLabel}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
